import * as Sentry from "@sentry/react";
import { getBrowserEnvSource } from "@/lib/runtime-config";

export type BrowserSentryConfig = {
  enabled: boolean;
  dsn: string | null;
  environment: string;
  release: string | null;
  tracesSampleRate: number;
};

let browserSentryInitialized = false;

function parseSampleRate(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

function isPlaceholderDsn(value: string) {
  const normalized = value.trim();
  if (!normalized || normalized === "<>") {
    return true;
  }

  if (
    /example\.ingest\.sentry\.io/i.test(normalized)
    || /your[-_]?sentry/i.test(normalized)
  ) {
    return true;
  }

  try {
    const url = new URL(normalized);
    return url.hostname.toLowerCase() === "example.ingest.sentry.io";
  } catch {
    return true;
  }
}

function hasConfiguredValue(value: string | null | undefined) {
  return Boolean(value && !isPlaceholderDsn(value));
}

export function getBrowserSentryConfig(
  env: Record<string, unknown> = getBrowserEnvSource(),
): BrowserSentryConfig {
  const dsn = typeof env.VITE_SENTRY_DSN === "string" ? env.VITE_SENTRY_DSN : null;
  const environment =
    typeof env.VITE_SENTRY_ENVIRONMENT === "string" && env.VITE_SENTRY_ENVIRONMENT.trim()
      ? env.VITE_SENTRY_ENVIRONMENT
      : typeof env.MODE === "string" && env.MODE.trim()
        ? env.MODE
        : "development";
  const release =
    typeof env.VITE_SENTRY_RELEASE === "string" && env.VITE_SENTRY_RELEASE.trim()
      ? env.VITE_SENTRY_RELEASE
      : typeof env.VERCEL_GIT_COMMIT_SHA === "string" && env.VERCEL_GIT_COMMIT_SHA.trim()
        ? env.VERCEL_GIT_COMMIT_SHA
        : null;

  return {
    dsn,
    enabled: hasConfiguredValue(dsn),
    environment,
    release,
    tracesSampleRate: parseSampleRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0),
  };
}

export function initBrowserSentry(
  env: Record<string, unknown> = getBrowserEnvSource(),
) {
  const config = getBrowserSentryConfig(env);

  if (!config.enabled || browserSentryInitialized) {
    return config;
  }

  Sentry.init({
    dsn: config.dsn ?? undefined,
    enabled: config.enabled,
    environment: config.environment,
    release: config.release ?? undefined,
    tracesSampleRate: config.tracesSampleRate,
  });
  browserSentryInitialized = true;
  return config;
}

export function captureBrowserException(
  error: unknown,
  context: {
    component?: string;
    extras?: Record<string, unknown>;
    tags?: Record<string, string>;
  } = {},
) {
  if (!browserSentryInitialized) {
    return;
  }

  Sentry.captureException(error, {
    extra: context.extras,
    tags: {
      component: context.component,
      ...context.tags,
    },
  });
}

export function resetBrowserSentryForTests() {
  browserSentryInitialized = false;
}
